
// ProductDetails.jsx
// ProductDetails.jsx
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  Star,
  Sparkles,
  ArrowUpRight,
  Zap,
  Shield,
  TrendingUp,
  CircleCheck,
} from "lucide-react";
import { productsData } from "./ProductsData";
import DemoBookingAssistant from "../Chatbot/Demobookingassistant";
import IntegrationStack from "../Home/IntegrationStack";


/* ─── Noise / grid SVG background ───────────────────────────── */
const NoiseBg = () => (
  <svg
    className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.03]"
    xmlns="http://www.w3.org/2000/svg"
  >
    <filter id="noise">
      <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
      <feColorMatrix type="saturate" values="0" />
    </filter>
    <rect width="100%" height="100%" filter="url(#noise)" />
  </svg>
);

/* ─── Pill badge ─────────────────────────────────────────────── */
const Badge = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-amber-400">
    <Sparkles size={11} />
    {children}
  </span>
);

/* ─── Section title ──────────────────────────────────────────── */
const SectionTitle = ({ sup, title, sub, light = false }) => (
  <div className="mb-14 text-center">
    {sup && <Badge>{sup}</Badge>}
    <h2
      className={`mt-4 text-2xl lg:text-4xl font-bold tracking-wide ${
        light ? "text-white" : "text-slate-900"
      }`}
    >
      {title}
    </h2>
    {sub && (
      <p className={`mx-auto mt-4 max-w-xl text-sm md:text-base leading-relaxed ${light ? "text-slate-300" : "text-slate-500"}`}>
        {sub}
      </p>
    )}
  </div>
);

/* ─── Main component ─────────────────────────────────────────── */
const ProductDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const heroRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const product = productsData.find((item) => item.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="text-center">
          <p className="text-6xl font-black text-slate-800">404</p>
          <h2 className="mt-4 text-2xl font-bold text-white">Product Not Found</h2>
          <button
            onClick={() => navigate("/")}
            className="mt-6 rounded-full bg-amber-400 px-6 py-3 font-semibold text-slate-900 hover:bg-amber-300 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const iconMap = [Zap, Shield, TrendingUp, Star];

  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{ fontFamily: "'DM Sans', 'Inter', sans-serif", background: "#f8f7f4" }}
    >
      {/* ══════════════════ HERO ══════════════════ */}
      <section
        ref={heroRef}
        className="relative flex py-12 flex-col items-center justify-center overflow-hidden bg-slate-100 px-4 text-center pt-20"
      >
        <NoiseBg />

        {/* glowing orbs */}
        <div className="absolute left-1/4 top-1/4 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500/20 blur-[120px]" />
        <div className="absolute right-1/4 bottom-1/3 h-80 w-80 rounded-full bg-teal-500/15 blur-[100px]" />

        {/* grid lines */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-10 max-w-7xl">
          {/* headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 80 }}
            className="mt-6 text-xl md:text-3xl lg:text-4xl font-black leading-[1.05] tracking-tight text-gray-800"
          >
            {product.heading}
          </motion.h1>

          {/* tagline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mx-auto mt-6 max-w-2xl leading-relaxed text-slate-700 lg:text-lg"
          >
            {product.tagline}
          </motion.p>

          {/* CTA row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-10 flex flex-col md:flex-row items-center justify-center gap-4"
          >
             <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            className="flex items-center justify-center gap-3"
          >
           <span className="text-sm sm:text-base tracking-wide text-slate-700 uppercase">
              Our Product Partner -
             </span>
            <a
              href="https://hiview.ai/product-suite"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 rounded-full  border-slate-700 px-4 py-2 
                         backdrop-blur-md transition-all 
                        hover:border-slate-500 hover:bg-white/10"
            >
              <span className="text-orange-600 text-xs sm:text-sm font-semibold underline underline-offset-4 
                              decoration-dotted group-hover:decoration-solid transition-all">
                HiView AI
              </span>

              {/* Optional external link icon */}
              <ArrowUpRight 
                size={14} 
                className="text-orange-400 opacity-70 group-hover:opacity-100 transition"
              />
             </a>
            </motion.div>
            <button 
              onClick={()=> setIsOpen(true)}
            className="group inline-flex text-sm items-center gap-2 rounded-full bg-amber-400 px-8 py-3.5 font-bold text-slate-900 shadow-lg shadow-amber-400/20 transition-all hover:bg-amber-300 hover:shadow-amber-300/30">
              Request a Demo
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.div>
        </motion.div>

       
      </section>

      {/* ══════════════════ METRICS TICKER ══════════════════ */}
      <section className="border-y border-slate-200 bg-white py-12">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
            {product.metrics.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                viewport={{ once: true }}
                className="group relative text-center"
              >
                {/* vertical separator */}
                {i !== 0 && (
                  <div className="absolute -left-4 top-1/2 hidden h-12 w-px -translate-y-1/2 bg-slate-200 lg:block" />
                )}
                <p className="text-2xl font-black tracking-tight text-slate-900 transition-colors group-hover:text-amber-500 lg:text-4xl">
                  {m.value}
                </p>
                <p className="mt-2 text-sm font-medium  tracking-widest text-slate-400">{m.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════ OVERVIEW ══════════════════ */}
      <section className="bg-[#f8f7f4] py-12 px-4">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            {/* left */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge>Overview</Badge>
              <h2 className="mt-5 text-2xl font-black leading-tight tracking-tight text-slate-900 lg:text-4xl">
                {product.overViewTagLine}
              </h2>
              <p className="mt-6  leading-relaxed text-slate-500">{product.overViewDesc}</p>
            </motion.div>

            {/* right — capabilities card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="relative overflow-hidden rounded-3xl bg-slate-950 p-8 shadow-2xl">
                <NoiseBg />
                <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-amber-400/20 blur-[60px]" />
                <div className="relative z-10">
                  <p className="mb-6 text-xs font-semibold uppercase tracking-widest text-amber-400">
                    Key Highlights
                  </p>
                  <ul className="space-y-4">
                    {product.overViewOpints.map((cap, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.08 }}
                        viewport={{ once: true }}
                        className="flex items-center gap-3"
                      >
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-400/10 text-teal-400">
                          <CheckCircle size={14} />
                        </span>
                        <span className="text-slate-300">{cap}</span>
                      </motion.li>
                    ))}
                  </ul>

                  {/* decorative bottom bar */}
                  <div className="mt-8 flex gap-1.5">
                    {[40, 65, 50, 80, 55, 90, 70].map((h, i) => (
                      <motion.div
                        key={i}
                        initial={{ scaleY: 0 }}
                        whileInView={{ scaleY: 1 }}
                        transition={{ delay: 0.4 + i * 0.06, type: "spring" }}
                        viewport={{ once: true }}
                        className="flex-1 origin-bottom rounded-sm bg-amber-400/80"
                        style={{ height: h / 4 }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
 
      {/* ══════════════════ INTEGRATIONS ══════════════════ */}
     
     <section>
      <IntegrationStack/>
     </section>


          {/* ══════════════════ Capabilities ══════════════════ */}
      <section className="bg-gray-50 py-12 px-4">
  <div className="mx-auto max-w-7xl">
    <SectionTitle
      sup="Key Capabilities"
      title="What Powers Your Operations"
    />

    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mt-10">
      {product.capabilities.map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.12 }}
          viewport={{ once: true }}
          whileHover={{ y: -5 }}
          className="group"
        >
          <div className="border border-gray-300 bg-white rounded-2xl px-5 py-6 space-y-4 shadow-sm hover:shadow-lg transition-all duration-300 h-full">
            
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600">
              <CircleCheck size={22} />
            </div>

            <h3 className="text-gray-800 font-medium leading-relaxed">
              {item}
            </h3>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
</section>

       {/* ══════════════════ Business Impact ══════════════════ */}
       <section className="bg-white py-12 px-4">
        <div className="mx-auto max-w-7xl">
          <SectionTitle
            sup="Business Impact"
            title="Measurable Outcomes Delivered"
          />
          <div className="relative grid gap-8 md:grid-cols-2 lg:grid-cols-3">

            {product.impact.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.12 }}
                viewport={{ once: true }}
                className="group relative"
              >
              <div class="group flex items-start gap-3 bg-card rounded-xl border border-border p-4 hover:border-teal-500/30 hover:shadow-sm transition-all duration-300 h-full">
              <div class="w-8 h-8 rounded-lg bg-teal-800/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-teal-500 transition-all duration-300">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trending-up w-4 h-4 text-teal group-hover:text-white transition-colors duration-300"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>
              </div>
              <p class="text-foreground text-sm leading-relaxed">{item}</p>
              </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
        
      
      {/* ══════════════════ USE CASES ══════════════════ */}
      <section className="bg-[#f8f7f4] py-12 px-4">
        <div className="mx-auto max-w-6xl">
          <SectionTitle
            sup="Use Cases"
            title="Real-World Applications"
            sub="Industry-specific solutions that drive measurable results"
          />
          <div className="grid gap-6 md:grid-cols-3">
            {product.useCases.map((uc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.92 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
                className="group relative overflow-hidden rounded-3xl bg-white p-8 shadow-sm transition-all hover:shadow-xl"
              >
                {/* accent corner */}
                <div className="absolute right-0 top-0 h-24 w-24 translate-x-8 -translate-y-8 rounded-full bg-amber-400/15 blur-2xl transition-all group-hover:bg-amber-400/25" />
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-teal-400 shadow-lg shadow-amber-400/20">
                  <Star className="text-white" size={20} />
                </div>
                <h3 className="mb-3 text-lg font-bold text-slate-900">{uc.title}</h3>
                <p className="leading-relaxed text-slate-500">{uc.description}</p>
               
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════ GET STARTED ══════════════════ */}
      <section className="bg-white py-12 px-4">
        <div className="mx-auto max-w-6xl">
          <SectionTitle
            sup={product.getStarted.badge}
            title={product.getStarted.headline}
            sub={product.getStarted.subtitle}
          />
          <div className="relative grid gap-8 md:grid-cols-3">
            {/* connector line */}
            <div className="absolute top-10 left-[16.66%] right-[16.66%] hidden h-px bg-gradient-to-r from-amber-400/40 via-teal-400/40 to-amber-400/40 md:block" />

            {product.getStarted.steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.12 }}
                viewport={{ once: true }}
                className="group relative"
              >
                {/* step number bubble */}
                <div className="relative mx-auto mb-6 flex h-20 w-20 items-center justify-center">
                  <div className="absolute inset-0 rounded-full border-2 border-dashed border-slate-200 transition-all group-hover:border-amber-400/60" />
                  <span className="text-3xl font-black text-slate-900">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mb-3 text-center text-xl font-bold text-slate-900">{step.title}</h3>
                <p className="text-center leading-relaxed text-slate-500">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* ══════════════════ WHY CHOOSE US ══════════════════ */}
      <section className="bg-[#f8f7f4] py-12 px-4">
        <div className="mx-auto max-w-6xl">
          <SectionTitle
            sup="Why Us"
            title={product.whyChoose.headline}
            sub={product.whyChoose.subtitle}
          />
          <div className="grid gap-6 md:grid-cols-2">
            {product.whyChoose.items.map((item, i) => {
              const Icon = iconMap[i % iconMap.length];
              const isAccent = item.variant === "accent";
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -4 }}
                  className={`group relative overflow-hidden rounded-3xl p-8 transition-shadow hover:shadow-2xl bg-white text-slate-900`}
                >
                  {isAccent && <NoiseBg />}
                  <div
                    className={`relative z-10 mb-5 flex h-12 w-12 items-center justify-center bg-amber-400/10 rounded-2xl `}
                  >
                    <Icon size={22} className="text-amber-400" />
                  </div>
                  <h3 className="relative z-10 mb-3 text-2xl font-bold">{item.title}</h3>
                  <p className={`relative z-10 leading-relaxed ${isAccent ? "text-slate-400" : "text-slate-500"}`}>
                    {item.desc}
                  </p>
                  {isAccent && (
                    <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-amber-400/10 blur-[50px]" />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

       {/* ══════════════════ PLATFORM HIGHLIGHT ══════════════════ */}
      <section className="relative overflow-hidden bg-slate-950 py-12 px-4">
        <NoiseBg />
        <div className="absolute left-0 top-0 h-64 w-64 rounded-full bg-teal-500/10 blur-[80px]" />
        <div className="absolute right-0 bottom-0 h-64 w-64 rounded-full bg-amber-500/10 blur-[80px]" />

        <div className="relative mx-auto max-w-7xl xl:px-10">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            {/* stats grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {product.platformHighlight.stats.map((s, i) => (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.04 }}
                  className={`rounded-2xl p-6 text-center text-white bg-slate-800/60 border border-slate-700`}
                >
                  <p className="text-2xl sm:text-3xl font-black">{s.value}</p>
                  <p className={`mt-1 text-sm font-medium text-slate-400`}>
                    {s.label}
                  </p>
                </motion.div>
              ))}
            </motion.div>

            {/* copy */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge>{product.platformHighlight.subheadline}</Badge>
              <h2 className="mt-5 text-2xl font-black leading-tight text-white lg:text-4xl">
                {product.platformHighlight.headline}
              </h2>
              <p className="mt-5 leading-relaxed text-slate-400">
                {product.platformHighlight.description}
              </p>
              <ul className="mt-6 space-y-3">
                {product.platformHighlight.bullets.map((b, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.08 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-3 text-slate-300"
                  >
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                    {b}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

     

      {/* ══════════════════ CTA ══════════════════ */}
      <section className="relative overflow-hidden bg-slate-950 py-12 px-4">
        <NoiseBg />
        {/* blobs */}
        <div className="absolute left-1/4 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full bg-amber-400/20 blur-[80px]" />
        <div className="absolute right-1/4 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full bg-teal-500/15 blur-[80px]" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative mx-auto max-w-3xl text-center"
        >
          {/* <p className="text-sm font-semibold uppercase tracking-widest text-amber-400">Get started today</p> */}
          <h2 className="mt-5 text-2xl lg:text-4xl font-black text-white">
            Ready to get started with <span className="text-orange-700">{product.heading}</span>?
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-sm md:text-base text-slate-400">
            {/* Join industry leaders who are already seeing measurable results with our platform. */}
            See how {product.heading} can transform your operations with a personalized demo.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-5 text-gray-300 mt-4">
          {["Free trial available", "Full platform access", "Dedicated onboarding"].map((item,i)=>(
            <li className="flex text-sm sm:text-lg items-center justify-center gap-2">
              <i className="text-yellow-600"><CircleCheck /></i>
              <span>{item}</span>
              
            </li>
          ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <button 
            onClick={()=>{
              navigate("/contact");
              window.scrollTo({
                top:0,
                behavior: "smooth"
              })
            }}
            className="group text-xs sm:text-sm inline-flex items-center gap-2 rounded-full bg-amber-400 px-8 py-4 font-bold text-slate-900 shadow-lg shadow-amber-400/20 transition-all hover:bg-amber-300">
              Contact Us
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button 
            onClick={()=> setIsOpen(true)}
            className="rounded-full text-xs sm:text-sm border border-slate-700 px-8 py-4 font-semibold text-slate-300 transition-all hover:border-slate-500 hover:text-white">
              Schedule a Demo
            </button>
          </div>
        </motion.div>
      </section>

      <DemoBookingAssistant
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            autoStart={true}
          />
    </div>
  );
};

export default ProductDetails; 